import { ApolloError, useLazyQuery, useMutation } from '@apollo/client'
import { isAfter, secondsToMilliseconds } from 'date-fns'
import { Formik, FormikHelpers, FormikProps } from 'formik'
import { isArray, isEmpty } from 'lodash'
import { ReactElement, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { NavLink } from 'react-router-dom'
import styled from 'styled-components'

import { FormattedDate } from '@10xLabs/react-web/components/FormattedDate'
import { Column } from 'components/v2/Column'
import { Row } from 'components/v2/Row'
import FormLayoutV2 from 'components/v2/FormLayout'
import { AddCircleIcon } from 'components/Icons/AddCircleIcon'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import Heading from 'components/v2/Heading'
import { Button } from 'components/v2/Button'
import { Checkbox } from 'components/v2/Checkbox'
import IconButton from 'components/v2/IconButton'
import { IsActive } from 'components/Chip'
import { IChangeRouteServiceRouteForm } from 'features/Routes/components/EditRoute/RouteForm/types'
import {
  GTCreateTRouteResponse,
  GTPlace,
  GTPlaceSuggestion,
  GTRouteQueryInput,
  GTRouteServiceCompanyLine,
  GTRouteServiceItinerarySchedule,
  GTService,
  GTTRoute
} from 'gql/types.generated'
import { GET_ROUTE_WITH_STOPS_V2 } from 'features/Routes/queries-legacy'
import { getTimeAsMinutes, parseDate } from 'utils/date'
import { getUnprocessableErrors } from 'utils/error'
import { schema } from './schema'
import {
  EItineraryStatus,
  getItineraryStatus
} from 'features/Services/components/NewServicesV2/components/ItineraryStatus'
import { mapItineraryScheduleToItinerary, mapServiceQueryToEditForm } from 'models/services'
import { CREATE_ROUTE_V2 } from 'features/Routes/mutations-legacy'
import { ItineraryNotificationContainer } from 'features/Services/components/NewServicesV2/components/ItineraryNotification'
import { pad2 } from 'utils/string'
import { getMaxActivePeriod, getRemaingSecondsBeforeEditing, isEditPlanningCoolingDown } from 'features/Services/utils'
import { RouteStops } from 'features/Routes/components/RouteStops/RouteStops'
import { encodeRouteShape, getTotalDistanceByRoute } from 'utils/google-maps'
import { routeFormUtils } from 'features/Routes/utils'
import { useGoogleMapsScript } from 'hooks/google-maps'
import { RouteMap } from 'features/Routes/components/RouteMap'
import { IRouteStop } from 'features/Routes/types'

const StopsTitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const Table = styled.table`
  border-collapse: separate;
  width: 100%;
  border-spacing: 0px;
`

const Th = styled.th`
  color: #717171;
  font-weight: 400;
  font-size: 16px;
  border-bottom: 2px solid #b0b0b0;
  text-align: left;
  text-transform: uppercase;
  padding: 0 8px 16px;
`

const CheckboxTh = styled(Th)`
  width: 16px;
  padding: 0 0 16px;
`

const Tr = styled.tr<{ isSelected?: boolean; isDisabled?: boolean }>`
  background-color: ${({ isSelected }): TSFixMe => (isSelected ? '#dcecff' : 'none')};
  cursor: ${({ isDisabled }): TSFixMe => (isDisabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ isDisabled }): TSFixMe => (isDisabled ? '0.3' : '1')};

  :hover {
    box-shadow: 0 2px 7px 0 rgba(0, 0, 0, 0.16);
  }
`

const Td = styled.td<{ bold?: boolean }>`
  color: #28265d;
  padding: 18px 8px;
  vertical-align: middle;
  border-bottom: 1px solid #eae9e9;
  font-weight: ${({ bold }): string => (bold ? '600' : '400')};
  font-size: 16px;
`

const CheckboxTd = styled(Td)`
  padding: 0px;
`

const reorder = (list: TSFixMe, startIndex: number, endIndex: number): TSFixMe[] => {
  const result = [...list]
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)

  return result
}

const getLastPriceCheckedDate = (services: IChangeRouteServiceRouteForm[]): Date | undefined => {
  return services.reduce((result: Date | undefined, service: IChangeRouteServiceRouteForm): Date | undefined => {
    if (!result) return service.priceCheckedDate
    if (service.priceCheckedDate && isAfter(service.priceCheckedDate, result)) return service.priceCheckedDate
    return result
  }, undefined)
}

let interval: NodeJS.Timeout

interface IFormProps extends FormikProps<TSFixMe> {
  oldRoute: GTTRoute
  isReplacingPlaces?: boolean
  error?: ApolloError
  loading?: boolean
  setIsReplacingPlaces: (isReplacingPlaces: boolean) => void
}

const Form = ({
  values,
  touched,
  errors,
  handleBlur,
  handleSubmit,
  setFieldValue,
  setValues,
  error,
  loading,
  oldRoute,
  isReplacingPlaces,
  setIsReplacingPlaces
}: IFormProps): ReactElement => {
  const intl = useIntl()
  const lastPriceCheckedDate: Date | undefined = getLastPriceCheckedDate(values.services)
  const maxActivePeriod = getMaxActivePeriod(values.services)

  const [timer, setTimer] = useState(getRemaingSecondsBeforeEditing(lastPriceCheckedDate))
  const [editRouteDisabled, setEditRouteDisabled] = useState(
    isEditPlanningCoolingDown(lastPriceCheckedDate, maxActivePeriod)
  )
  const [calculateShapeError, setCalculateShapeError] = useState<Error>()

  useEffect(() => {
    const lastPriceCheckedDate: Date | undefined = getLastPriceCheckedDate(values.services)
    if (isEditPlanningCoolingDown(lastPriceCheckedDate, maxActivePeriod)) {
      interval = setInterval(() => {
        if (!isEditPlanningCoolingDown(lastPriceCheckedDate, maxActivePeriod)) {
          clearInterval(interval)
          setEditRouteDisabled(false)
        } else {
          setTimer(getRemaingSecondsBeforeEditing(lastPriceCheckedDate))
        }
      }, secondsToMilliseconds(1))
    }
  }, [values.services])

  const addStop = (): void => {
    setValues({
      ...values,
      stops: [
        ...values.stops,
        {
          id: '',
          name: '',
          timezone: ''
        }
      ],
      encodedShape: '',
      distance: ''
    })
    setIsReplacingPlaces(false)
  }

  const handleRemoveStop = (stopIndex: number): void => {
    setValues({
      ...values,
      stops: values.stops.filter((stop: GTPlace, index: number) => {
        return index !== stopIndex
      }),
      encodedShape: '',
      distance: ''
    })
    setIsReplacingPlaces(false)
  }

  const handleChangeStop = (newPlace: GTPlaceSuggestion, stopIndex: number): void => {
    setValues({
      ...values,
      stops: values.stops.map((stop: GTPlaceSuggestion, index: number) => {
        return index === stopIndex ? newPlace : stop
      }),
      encodedShape: '',
      distance: ''
    })
    if (typeof isReplacingPlaces === 'undefined') {
      setIsReplacingPlaces(true)
    }
  }

  const handleDragEnd = (result: TSFixMe): void => {
    if (!result.destination) {
      // dropped outside the list
      return
    }
    const stopsReordered = reorder(values.stops, result.source.index, result.destination.index)
    setValues({
      ...values,
      stops: stopsReordered,
      encodedShape: '',
      distance: ''
    })
    setIsReplacingPlaces(false)
  }

  const handleCalculateShape = (directionsRoute: google.maps.DirectionsRoute): void => {
    setCalculateShapeError(undefined)
    const distance = getTotalDistanceByRoute(directionsRoute)
    const encodedShape = encodeRouteShape(directionsRoute)
    setValues({
      ...values,
      encodedShape,
      distance
    })
  }

  const handleCalculateShapeError = (error: Error): void => {
    setCalculateShapeError(error)
  }

  const shouldFilterPlacesByBusinessClient = !isEmpty(oldRoute.businessClient)

  return (
    <FormLayoutV2.Content as="form" autoComplete="off" onSubmit={handleSubmit}>
      {editRouteDisabled && (
        <ItineraryNotificationContainer>
          <FormattedMessage
            id="route.edit.form.coolingDown.description"
            defaultMessage="You need to wait {timer} before editing this route again."
            values={{ timer: `${pad2(timer.minutes ?? 0)}:${pad2(timer.seconds ?? 0)}` }}
          />
        </ItineraryNotificationContainer>
      )}
      <FormLayoutV2.Card>
        <StopsTitleWrapper>
          <Heading.H5>
            <FormattedMessage id="routes.edit.form.stops.title" defaultMessage="Stops" />
          </Heading.H5>
          {isReplacingPlaces !== true && <IconButton icon={<AddCircleIcon />} onClick={addStop} />}
        </StopsTitleWrapper>
        <Row>
          <Column width="50%">
            <RouteStops
              onDelete={handleRemoveStop}
              onChange={handleChangeStop}
              stops={values.stops}
              handleBlur={handleBlur}
              touched={touched}
              errors={errors}
              checkCanEditStop={(stop: GTPlaceSuggestion): boolean => {
                return !oldRoute.stops.some((oldStop) => oldStop.id === stop.id)
              }}
              isReplacingPlaces={isReplacingPlaces}
              handleDragEnd={handleDragEnd}
              companyId={shouldFilterPlacesByBusinessClient ? oldRoute.company.id : undefined}
              businessClientId={shouldFilterPlacesByBusinessClient ? oldRoute.businessClient?.id : undefined}
              businessSiteId={shouldFilterPlacesByBusinessClient ? oldRoute.businessSite?.id : undefined}
              vertical={oldRoute.company.vertical}
            />
            {values.distance ? (
              <p>
                <FormattedMessage
                  id="routes.new.form.destination.label"
                  defaultMessage="<strong>Kilometers of travel on the route</strong>: <em>{distance} km</em>"
                  values={{
                    distance: routeFormUtils.getDistanceLabel(values),
                    strong: (msg) => <strong>{msg}</strong>,
                    em: (msg) => <em>{msg}</em>
                  }}
                />
              </p>
            ) : null}
            {calculateShapeError ? (
              <p>
                <ErrorMessage>
                  {calculateShapeError.message ? (
                    calculateShapeError.message
                  ) : (
                    <FormattedMessage
                      id="routes.edit.form.calculateShape.error"
                      defaultMessage="There was an error while calculating the route shape"
                    />
                  )}
                </ErrorMessage>
              </p>
            ) : null}
            {touched.distance && errors.distance ? (
              <p>
                <ErrorMessage>{errors.distance}</ErrorMessage>
              </p>
            ) : null}
          </Column>
          <Column width="50%">
            <RouteMap
              stops={values.stops.map((stop: GTPlaceSuggestion): IRouteStop => {
                return {
                  id: stop.id,
                  name: stop.name,
                  position: {
                    lat: parseFloat(stop.location?.latitude || ''),
                    lng: parseFloat(stop.location?.longitude || '')
                  }
                }
              })}
              encodedShapePath={values.encodedShape ?? ''}
              onCalculateShape={handleCalculateShape}
              mapCenterLocation={{ lat: 19.4326, lng: -99.1332 }}
              onCalculateShapeError={handleCalculateShapeError}
            />
          </Column>
        </Row>
        {touched.stops && errors.stops && !isArray(errors.stops) && (
          <Row>
            <Column>
              <ErrorMessage>{errors.stops}</ErrorMessage>
            </Column>
          </Row>
        )}
      </FormLayoutV2.Card>
      <FormLayoutV2.Card>
        <Heading.H5>
          <FormattedMessage id="routes.edit.form.sercices.select.title" defaultMessage="Select services" />
        </Heading.H5>
        <Table>
          <thead>
            <tr>
              <CheckboxTh />
              <Th>
                <FormattedMessage id="dictionary.code" defaultMessage="Code" />
              </Th>
              <Th>
                <FormattedMessage id="dictionary.startDate" defaultMessage="Start date" />
              </Th>
              <Th>
                <FormattedMessage id="dictionary.endDate" defaultMessage="End date" />
              </Th>
              <Th>
                <FormattedMessage id="routes.change.services.activeItineraries" defaultMessage="Active itineraries" />
              </Th>
              <Th>
                <FormattedMessage id="dictionary.line" defaultMessage="Line" />
              </Th>
              <Th>
                <FormattedMessage id="dictionary.status" defaultMessage="Status" />
              </Th>
            </tr>
          </thead>
          <tbody>
            {values.services.map((service: IChangeRouteServiceRouteForm, index: number): ReactElement => {
              const activeItineraries = service.itinerariesSchedule.filter(
                (itinerarySchedule) =>
                  getItineraryStatus(
                    mapItineraryScheduleToItinerary(itinerarySchedule),
                    //@ts-expect-error: FIXME Argument of type 'IEditServiceForm' is not assignable to parameter of type 'IService'.
                    mapServiceQueryToEditForm(service)
                  ) === EItineraryStatus.Active
              )
              return (
                <Tr
                  key={index}
                  isSelected={service.isSelected}
                  onClick={(): TSFixMe => {
                    setFieldValue(`services[${index}].isSelected`, !service.isSelected)
                  }}
                >
                  <CheckboxTd>
                    <Checkbox
                      id={`services[${index}].isSelected`}
                      checked={service.isSelected}
                      // eslint-disable-next-line @typescript-eslint/no-empty-function
                      onChange={(): TSFixMe => {}}
                      onClick={(event): TSFixMe => event.stopPropagation()}
                    />
                  </CheckboxTd>
                  <Td bold>{service.code}</Td>
                  <Td>
                    <FormattedDate
                      intl={intl}
                      value={parseDate(service.startAt)}
                      month="2-digit"
                      day="2-digit"
                      year="numeric"
                    />
                  </Td>
                  <Td>
                    {service.endAt ? (
                      <FormattedDate
                        intl={intl}
                        value={parseDate(service.endAt)}
                        month="2-digit"
                        day="2-digit"
                        year="numeric"
                      />
                    ) : (
                      '-'
                    )}
                  </Td>
                  <Td>{activeItineraries.length}</Td>
                  <Td>{service.companyLine.name}</Td>
                  <Td>
                    <IsActive isActive={service.isActive} />
                  </Td>
                </Tr>
              )
            })}
          </tbody>
        </Table>
      </FormLayoutV2.Card>
      {error && (
        <FormLayoutV2.Actions>
          <ErrorMessage errors={getUnprocessableErrors(error)}>
            <FormattedMessage
              id="routes.edit.form.error"
              defaultMessage="There was an error while editing your route"
            />
          </ErrorMessage>
        </FormLayoutV2.Actions>
      )}
      <FormLayoutV2.Actions>
        <Button as={NavLink} to="/routes" kind="cancel">
          <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
        </Button>
        <Button type="submit" kind="primary" disabled={editRouteDisabled} loading={loading}>
          <FormattedMessage id="dictionary.next" defaultMessage="Next" />
        </Button>
      </FormLayoutV2.Actions>
    </FormLayoutV2.Content>
  )
}

const hasStopsDiff = (a: TSFixMe, b: TSFixMe): TSFixMe => {
  if (a.length !== b.length) {
    return true
  }

  const stopsDiff = a.filter((id: TSFixMe, index: TSFixMe): TSFixMe => {
    return id !== b[index]
  })

  return !isEmpty(stopsDiff)
}

const getFirstDepartureTimeByItinerariesSchedule = (itinerariesSchedule: GTRouteServiceItinerarySchedule[]): string => {
  return itinerariesSchedule[0].itinerary[0].departure?.time ?? ''
}

const getCompanyLinesByServices = (services: GTService[]): GTRouteServiceCompanyLine[] => {
  return services.reduce((acc: GTRouteServiceCompanyLine[], service: GTService): GTRouteServiceCompanyLine[] => {
    if (acc.some((companyLine: GTRouteServiceCompanyLine): boolean => companyLine.id === service.companyLine.id)) {
      return acc
    }

    return [...acc, service.companyLine]
  }, [])
}

interface IRouteFormProps {
  route: GTTRoute
  services: GTService[]
  onSubmit: (values: TSFixMe) => void
  scrollToTop?: () => void
  isReplacingPlaces?: boolean
  setIsReplacingPlaces: (isReplacingPlaces: boolean) => void
}
export const RouteForm = ({
  route,
  services,
  onSubmit,
  scrollToTop,
  isReplacingPlaces,
  setIsReplacingPlaces
}: IRouteFormProps): ReactElement => {
  const intl = useIntl()
  useEffect(() => {
    scrollToTop?.()
  }, [])

  const initialValues = {
    stops: route.stops || [],
    services:
      services
        .map((service: GTService): IChangeRouteServiceRouteForm => {
          return {
            ...service,
            priceCheckedDate: service.priceCheckedAt ? new Date(service.priceCheckedAt) : undefined,
            isSelected: false
          }
        })
        .sort((a: IChangeRouteServiceRouteForm, b: IChangeRouteServiceRouteForm) => {
          const countA = getTimeAsMinutes(getFirstDepartureTimeByItinerariesSchedule(a.itinerariesSchedule))
          const countB = getTimeAsMinutes(getFirstDepartureTimeByItinerariesSchedule(b.itinerariesSchedule))

          if (countA < countB) {
            return -1
          } else if (countA > countB) {
            return 1
          }

          return 0
        }) || [],
    encodedShape: route.polyline?.encoded,
    distance: route.polyline?.distance
  }

  const [error, setError] = useState<TSFixMe>()
  const [getRoute] = useLazyQuery<{ troute: GTTRoute }, GTRouteQueryInput>(GET_ROUTE_WITH_STOPS_V2, {
    fetchPolicy: 'network-only'
  })

  const [createRoute] = useMutation<{ createTRoute: GTCreateTRouteResponse }>(CREATE_ROUTE_V2)
  useGoogleMapsScript()

  const handleSubmit = async (values: TSFixMe, { setSubmitting }: FormikHelpers<TSFixMe>): Promise<void> => {
    setError(null)
    const canGoNext =
      values.services.filter((service: TSFixMe) => service.isSelected).length > 0 &&
      hasStopsDiff(
        route.stops.map(({ id }: TSFixMe) => id),
        values.stops.map(({ id }: TSFixMe) => id)
      )

    if (!canGoNext) {
      setSubmitting(false)
      return
    }

    try {
      const { data } = await getRoute({
        variables: {
          stopIDs: values.stops.map(({ id }: { id: string }) => id),
          companyID: route.company.id
        }
      })
      const existingRoute = data?.troute
      const selectedServices = values.services.filter((service: TSFixMe) => service.isSelected)
      const companyLinesByServices = getCompanyLinesByServices(selectedServices)

      if (isEmpty(existingRoute)) {
        const stopIds = values.stops.map(({ id }: TSFixMe) => id)
        const { data } = await createRoute({
          variables: {
            input: {
              name: route.name,
              companyID: route.company.id,
              businessClientID: route.businessClient?.id || undefined,
              businessSiteID: route.businessSite?.id || undefined,
              stopIDs: stopIds,
              companyLineIDs: companyLinesByServices.map(({ id }) => id),
              polyline: {
                encoded: values.encodedShape,
                distance: values.distance
              }
            }
          }
        })
        onSubmit({
          route: {
            id: data?.createTRoute.id || '',
            companyLines: companyLinesByServices,
            company: route.company,
            stops: [...values.stops]
          },
          services: selectedServices
        })
      } else {
        onSubmit({
          route: {
            ...existingRoute,
            companyLines: companyLinesByServices,
            executeAt: values.executeAt
          },
          services: selectedServices
        })
      }
    } catch (error) {
      setError(error)
    }
    setSubmitting(false)
  }

  return (
    <Formik initialValues={initialValues} validationSchema={schema(intl)} onSubmit={handleSubmit}>
      {(props): ReactElement => {
        return (
          <Form
            {...props}
            error={error}
            loading={props.isSubmitting}
            oldRoute={route}
            isReplacingPlaces={isReplacingPlaces}
            setIsReplacingPlaces={setIsReplacingPlaces}
          />
        )
      }}
    </Formik>
  )
}
